import { useEffect, useState } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import Switch from "../../../components/switch/Switch";
import { createDateQuery, createSingleQuery, createTextSearchQuery, SearchQuery } from "../../../services/api/search";
import CustomerTypeSelect from "../../../components/data/customer/CustomerTypeSelect";
import { isOdooConnected, odooSyncQueries } from "../../../services/odoo/odoo";

interface CustomerFilterFormProps {
  /*
   * Le type des filtres, pas celui du client : `odooNotSynced` est une question posée sur le
   * client, pas un de ses champs. C'est aussi ce que Customers.tsx déclare déjà à son drawer.
   */
  data?: Partial<EasyTech.CustomerFilterType>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}


export const getQueriesFromFilter = (filters: Partial<EasyTech.CustomerFilterType>):SearchQuery[] => {
  const queries: SearchQuery[] = [];
  
  if (filters.id) {
    queries.push(createSingleQuery("id", filters.id));
  } else {
    if (filters.name) {
      queries.push(createTextSearchQuery(["name"], filters.name));
    }

    if (filters.contactInfo?.email?.trim()) {
      // Le DTO expose `email`, la propriété JPA est `mail`. EQUAL sur `mail`
      // (ou sur `email`, même prédicat) couvre aussi la liste `emails`.
      queries.push(
        createSingleQuery("contactInfo.mail", filters.contactInfo.email.trim())
      );
    }

    if (filters.contactInfo?.phone) {
      queries.push(
        createSingleQuery("contactInfo.phone", filters.contactInfo.phone)
      );
    }

    
    if (filters.type && filters.type.length > 0) {
      const pro = filters.type.includes("PROSPECT");
      const act = filters.type.includes("ACTIVE");
      const ina = filters.type.includes("INACTIVE");
      if (pro && !act && !ina)
        queries.push(createSingleQuery("lastWorkOrder", null, "IS_NULL"));
      if (pro && act && !ina)
        queries.push(createDateQuery("lastWorkOrder", new Date(new Date().setFullYear(new Date().getFullYear() - 1)), "GREATER_THAN_OR_NULL"));
      if (pro && !act && ina)
        queries.push(createDateQuery("lastWorkOrder", new Date(new Date().setFullYear(new Date().getFullYear() - 1)), "LESS_THAN_OR_NULL"));
      if (!pro && act && !ina)
        queries.push(createDateQuery("lastWorkOrder", new Date(new Date().setFullYear(new Date().getFullYear() - 1)), "GREATER_THAN"));
      if (!pro && act && ina)
        queries.push(createSingleQuery("lastWorkOrder", null, "IS_NOT_NULL"));
      if (!pro && !act && ina)
        queries.push(createDateQuery("lastWorkOrder", new Date(new Date().setFullYear(new Date().getFullYear() - 1)), "LESS_THAN"));
    }

    if (filters.nip) {
      queries.push(createTextSearchQuery(["nip"], filters.nip));
    }

    // Les deux filtres Odoo, identiques à ceux de la liste pièces — voir odooSyncQueries.
    queries.push(...odooSyncQueries(filters));

    if (filters.disabled) {
      queries.push(createSingleQuery("enabled", false));
    }

    if (filters.purchaseOrderMandatory) {
      queries.push(createSingleQuery("purchaseOrderMandatory", true));
    }
  }
  return queries;
};

const CustomerFilterForm = (props: CustomerFilterFormProps) => {
  const [formData, setFormData] = useState<Partial<EasyTech.CustomerFilterType>>({
    contactInfo: {
      email: "",
      phone: "",
      fax: "",
      notes: ""
    }
  });

  useEffect(() => {
    if (props.data) {
      const initialData = {
        ...props.data,
        contactInfo: props.data.contactInfo || {
          email: "",
          phone: "",
          fax: "",
          notes: ""
        }
      };
      setFormData(initialData);
    } else {
      setFormData({
        contactInfo: {
          email: "",
          phone: "",
          fax: "",
          notes: ""
        }
      });
    }
  }, [props.data]);

  // Gestion des changements pour les champs simples
  const handleChange = (value: any, name: string) => {
    const tmp = structuredClone(formData);
    tmp[name as keyof Partial<EasyTech.CustomerFilterType>] = value;
    props.onChange(name, value);
    setFormData(tmp);
  };

  const handleContactInfoChange = (value: any, name: string) => {
    const tmp = structuredClone(formData);
    
    // Assurer que contactInfo existe
    if (!tmp.contactInfo) {
      tmp.contactInfo = {
        email: "",
        phone: "",
        fax: "",
        notes: ""
      };
    }
    
    if (name === "email" || name === "phone" || name === "fax" || name === "notes") {
      tmp.contactInfo[name] = value;
    }
    
    setFormData(tmp);
    
    props.onChange("contactInfo", tmp.contactInfo);
  };

  /**
   * Activer « non synchronisés » vide l'identifiant saisi plutôt que de le garder inerte : sinon
   * une puce de filtre continuerait d'annoncer un identifiant qui ne participe plus à la recherche.
   *
   * @param checked - nouvel état de l'interrupteur
   */
  const handleNotSyncedChange = (checked: boolean) => {
    if (checked) handleChange("", "externalId");
    handleChange(checked, "odooNotSynced");
  };

  const submit = (e: any) => {
    e.preventDefault();
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-customer-filter"}>
      <div className="grid grid-cols-1 gap-4">
        
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:form:fields:name:label")}</label>
          <Input
            value={formData.name || ""}
            onChange={(e) => handleChange(e.target.value, "name")}
            className="w-full"
            size="md"
            placeholder={t("client:form:fields:name:placeholder")}
          />
        </div>
        
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:form:fields:mail:label")}</label>
          <Input
            value={formData.contactInfo?.email || ""}
            onChange={(e) => handleContactInfoChange(e.target.value, "email")}
            className="w-full"
            size="md"
            placeholder={t("client:form:fields:mail:placeholder")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:form:fields:phone:label")}</label>
          <Input
            value={formData.contactInfo?.phone || ""}
            onChange={(e) => handleContactInfoChange(e.target.value, "phone")}
            className="w-full"
            size="md"
            placeholder={t("client:form:fields:phone:placeholder")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:form:fields:nip:label")}</label>
          <Input
            value={formData.nip || ""}
            onChange={(e) => handleChange(e.target.value, "nip")}
            className="w-full"
            size="md"
            placeholder={t("client:form:fields:nip:placeholder")}
          />
        </div>

        {/* Les filtres Odoo n'existent que sur un realm relié à Odoo — même condition que la
            colonne de la liste et que le champ de la fiche client. */}
        {isOdooConnected() && (
          <>
            <div className="field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:odoo:externalId:label")}</label>
              <Input
                value={formData.externalId || ""}
                onChange={(e) => handleChange(e.target.value, "externalId")}
                disabled={!!formData.odooNotSynced}
                className="w-full"
                size="md"
                placeholder={t("client:odoo:externalId:placeholder")}
              />
            </div>

            <div className="field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:odoo:filters:notSynced")}</label>
              <Switch checked={!!formData.odooNotSynced}
                onChange={handleNotSyncedChange}/>
            </div>
          </>
        )}

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:form:fields:disabled:label")}</label>
          <Switch checked={formData.disabled}
            onChange={(b) => handleChange(b, "disabled")}/>
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("client:filters:purchaseOrderMandatory")}</label>
          <Switch checked={formData.purchaseOrderMandatory}
            onChange={(b) => handleChange(b, "purchaseOrderMandatory")}/>
        </div>

        <div className="field flex justify-between">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:type:label")}</label>
          <CustomerTypeSelect value={formData.type} onChange={(b) => handleChange(b, "type")} />
        </div>
      </div>
    </form>
  );
};

export default CustomerFilterForm; 